import React, { useEffect, useMemo, useState } from 'react';
import { FiSearch, FiUser } from 'react-icons/fi';
import { Card, Input } from '../../../components/ui/Kit';

function AgentCard({ agent, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(agent)}
      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
        selected
          ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
          : 'border-gray-200 bg-white hover:border-primary/40'
      }`}
    >
      {agent.headshot_url ? (
        <img
          src={agent.headshot_url}
          alt=""
          width={48}
          height={48}
          loading="lazy"
          decoding="async"
          className="h-12 w-12 rounded-full object-cover"
        />
      ) : (
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
          <FiUser className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-gray-900">{agent.full_name}</p>
        {agent.title ? <p className="truncate text-xs text-gray-500">{agent.title}</p> : null}
      </div>
    </button>
  );
}

export default function Step0AgentPicker({
  agents,
  loading,
  error,
  selectedAgentId,
  selectedSplitAgentId,
  onSelectAgent,
  onSelectSplitAgent,
}) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(
      (a) =>
        a.full_name?.toLowerCase().includes(q) ||
        a.title?.toLowerCase().includes(q),
    );
  }, [agents, query]);

  const splitCandidates = useMemo(
    () => agents.filter((a) => a.id !== selectedAgentId),
    [agents, selectedAgentId],
  );

  useEffect(() => {
    if (selectedSplitAgentId && selectedSplitAgentId === selectedAgentId) {
      onSelectSplitAgent(null);
    }
  }, [selectedAgentId, selectedSplitAgentId, onSelectSplitAgent]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-primary" />
        <p className="text-sm text-gray-500">Loading agents…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Select Your Agent</h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose the professional who will receive this application. <span className="text-red-500">*</span>
        </p>
      </div>

      <div className="relative">
        <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          className="pl-9"
          placeholder="Search by name or title…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <Card className="p-6 text-center text-sm text-gray-500">No agents match your search.</Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {filtered.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              selected={selectedAgentId === agent.id}
              onSelect={onSelectAgent}
            />
          ))}
        </div>
      )}

      {selectedAgentId && splitCandidates.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-secondary">
            Split Agent (optional)
          </h3>
          <select
            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            value={selectedSplitAgentId || ''}
            onChange={(e) => onSelectSplitAgent(e.target.value || null)}
          >
            <option value="">None</option>
            {splitCandidates.map((a) => (
              <option key={a.id} value={a.id}>
                {a.full_name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
